/* eslint-disable prefer-rest-params */
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  getExhibitionSettings,
  getLanguage,
  getLoginTimestamp,
  getOperatorsSettings,
  getSelectedExhibitionId,
  isLoggedInAsAdmin,
  selectExhibitionSettingsIsReadOnlyBuyerAndAccountInfo,
  selectOperatorSettingsIsReadOnlyBuyerAndAccountInfo
} from '@app/app.reducer';
import { LoadStyle } from '@app/colorizer/colorizer.actions';
import { FormName } from '@core/data-access/models/form/form.model';
import { TicketSendingOption } from '@core/data-access/models/shared/ticket-sending-options.model';
import { isLoginSessionExpired } from '@core/utils/datetime-helpers';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { SendingOptionModel } from '@products/models/product-selection.model';
import { AppConstants } from '@shared/app-constants';
import { consoleLog, getUUID } from '@shared/app-utils';
import { ErrorHandlingService } from '@shared/error-handling/error-handling.service';
import { InputBase } from '@shared/forms/inputs/input-base.class';
import { CheckboxInput } from '@shared/forms/inputs/input-checkbox.class';
import { DropdownInput } from '@shared/forms/inputs/input-dropdown.class';
import { RadioInput } from '@shared/forms/inputs/input-radio.class';
import { TextInput } from '@shared/forms/inputs/input-text.class';
import { StatusBarService } from '@shared/status-bar/status-bar.service';
import { environment } from '@src/environments/environment';
import {
  GetRandomSponsor,
  ResetShoppingStartTime,
  SetExhibitionStyles,
  SetOrderUuid,
  SetShoppingStartTime
} from '@store/customization/customization.actions';
import {
  CssSkinModel,
  ExhibitionSettingModel,
  QuestionnaireDataSection,
  SponsorBannerModel,
  TicketSelectionDeliveryType
} from '@store/customization/customization.interfaces';
import { generateSkinCss } from '@store/customization/customization.styles';
import { prepareConfirmationCheckboxes } from '@store/customization/forms/confirmation-checkboxes-data';
import { HelperService } from '@store/helpers/helper.service';
import { SendingOptionActions } from '@store/products/product-selection/product-selection.actions';
import { FormInputsPayloadModel, StepName } from '@store/step-forms/step.interface';
import { SetInputs } from '@store/step-forms/steps-forms.actions';
import { getIsTranslationLoaded } from '@store/translation/translation.selectors';
import { map as _map, isEmpty, isNil, some } from 'lodash';
import { Observable, Subscription, catchError, combineLatest, filter, first, iif, map, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CustomizationService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly translateService = inject(TranslateService);
  private readonly helperService = inject(HelperService);
  private readonly errorHandlingService = inject(ErrorHandlingService);
  private readonly statusBarService = inject(StatusBarService);

  exhibitionSubscriptions: Subscription[] = [];

  exhibitionSettings$: Observable<ExhibitionSettingModel>;

  private selectedLanguage$: Observable<string>;

  constructor() {
    this.exhibitionSettings$ = this.store.pipe(select(getExhibitionSettings));
    this.selectedLanguage$ = this.store.pipe(select(getLanguage));

    this.setConfirmationCheckboxesOnTranslationChangeSubscription();
  }

  capitalizeFirstLetter(text) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  setShoppingStartTime() {
    this.store.dispatch(new SetShoppingStartTime());
  }

  resetShoppingStartTime() {
    this.store.dispatch(new ResetShoppingStartTime());
  }

  triggerOnExhibitionChange(eventId) {
    // first unsubscribe all subscriptions which were set with event change
    this.exhibitionSubscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });

    const orderUUID = getUUID();
    this.store.dispatch(new SetOrderUuid(orderUUID));
    consoleLog('OrderUuid for this session: ' + orderUUID);

    // transform custom exhibition data to specific data structures
    // and load exhibition specific data
    this.selectedLanguage$
      .pipe(first())
      .subscribe((countryCode) => this.store.dispatch(new GetRandomSponsor({ eventId, countryCode })));

    this.exhibitionSettings$
      .pipe(
        first((exhibitionSettings) => !isEmpty(exhibitionSettings)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((exhibitionSettings) => {
        /* sending options */
        const ticketSelectionDeliveryTypes: TicketSelectionDeliveryType[] =
          exhibitionSettings.ticketSelectionDeliveryTypes;

        const isAllToBuyerEnabled = some(
          ticketSelectionDeliveryTypes,
          ({ value, isEnabled }) => value === TicketSendingOption.AllToBuyer && isEnabled
        );

        const isRetrievalLinkEnabled = some(
          ticketSelectionDeliveryTypes,
          ({ value, isEnabled }) => value === TicketSendingOption.TicketRetrievalLink && isEnabled
        );

        const isMobilePerOwnerEnabled = some(
          ticketSelectionDeliveryTypes,
          ({ value, isEnabled }) => value === TicketSendingOption.MobilePerOwner && isEnabled
        );

        const isSelfRegistration = this.helperService.isSelfRegistration();
        const isMobile = this.helperService.isMobile();

        let isSendingOptionSelected = false;

        const ticketSendingOptions: SendingOptionModel[] = _map(
          ticketSelectionDeliveryTypes,
          (ticketSelectionDeliveryType: TicketSelectionDeliveryType) => {
            const { isEnabled, isBasic, value } = ticketSelectionDeliveryType;
            const ticketSendingOption: SendingOptionModel = {
              ...ticketSelectionDeliveryType,
              isSelected: false,
              isVisible: isEnabled
            };

            if (isSendingOptionSelected || !isEnabled || !isBasic) {
              return ticketSendingOption;
            }

            if (isSelfRegistration && value === TicketSendingOption.AllToBuyer) {
              ticketSendingOption.isSelected = isSendingOptionSelected = true;

              return ticketSendingOption;
            }

            if (isNil(isMobile)) {
              if (
                (value === TicketSendingOption.NormalPerOwner || value === TicketSendingOption.MobilePerOwner) &&
                (isAllToBuyerEnabled || isRetrievalLinkEnabled)
              ) {
                return ticketSendingOption;
              }

              ticketSendingOption.isSelected = isSendingOptionSelected = true;

              return ticketSendingOption;
            }

            if (value === TicketSendingOption.MobilePerOwner) {
              ticketSendingOption.isSelected = isSendingOptionSelected = true;

              return ticketSendingOption;
            }

            ticketSendingOption.isSelected = isSendingOptionSelected = isMobilePerOwnerEnabled ? false : true;

            return ticketSendingOption;
          }
        );

        const validatedSendingOptions = some(ticketSendingOptions, ({ isEnabled }) => isEnabled)
          ? ticketSendingOptions
          : _map(ticketSendingOptions, (ticketSendingOption) => {
              if (ticketSendingOption.value === TicketSendingOption.AllToBuyer) {
                return {
                  ...ticketSendingOption,
                  isEnabled: true,
                  isSelected: true
                };
              }

              return ticketSendingOption;
            });

        this.store.dispatch(SendingOptionActions.initSendingOptions(validatedSendingOptions));
      });
  }

  transformQuestionnaireIntoInput(questions: QuestionnaireDataSection[]): InputBase<any>[] {
    const sortedQuestions = questions.slice(0).map((question) => {
      const inputBase: any = {
        key: question.id.toString(),
        label: question.label,
        isQuestionnaire: true,
        required: question.isMandatory,
        translate: true,
        selectionLimit: question.selectionLimit,
        previousValueId: question.previousValueId
      };

      if (question.type === 'dropdown') {
        const sortedOptions = question.values.slice(0).sort((a, b) => a.order - b.order);

        inputBase.options = sortedOptions.map((option) => {
          return { key: option.id, value: option.text };
        });

        inputBase.cssClass = '';
        inputBase.columns = {
          xs: 12,
          sm: 12,
          md: question.previousValueId ? 6 : 12,
          lg: question.previousValueId ? 6 : 12,
          xl: question.previousValueId ? 6 : 12
        };

        return new DropdownInput(inputBase);
      } else if (question.type === 'text') {
        inputBase.key = question.id;
        inputBase['noTrim'] = true;

        if (question.values.length > 0) {
          inputBase.key += '_' + question.values[0].id;

          /* we need to translate the value */
          this.translateService
            .get(question.values[0].text)
            .pipe(filter((translation) => translation !== AppConstants.MISSING_TRANSLATION))
            .subscribe((translation: string) => {
              inputBase.placeholder = question.values[0].text;
            });
        }

        inputBase.maxLengthValidation = question.textLength || 150;
        inputBase.cssClass = '';
        inputBase.columns = {
          xs: 12,
          sm: 12,
          md: question.previousValueId ? 6 : 12,
          lg: question.previousValueId ? 6 : 12,
          xl: question.previousValueId ? 6 : 12
        };

        return new TextInput(inputBase);
      } else if (question.type === 'radio') {
        inputBase.cssClass = '';
        inputBase.columns = {
          xs: 12,
          sm: 12,
          md: 12,
          lg: 12,
          xl: 12
        };

        inputBase.options = question.values.map((option) => {
          return {
            key: option.id,
            value: option.text,
            order: option.order
          };
        });

        return new RadioInput(inputBase);
      } else if (question.type === 'checkbox') {
        inputBase.cssClass = '';
        inputBase.columns = {
          xs: 12,
          sm: 12,
          md: 12,
          lg: 12,
          xl: 12
        };

        inputBase.options = question.values.map((option) => {
          return {
            key: option.id,
            value: false,
            label: option.text,
            order: option.order
          };
        });

        return new CheckboxInput(inputBase);
      }
    });

    return sortedQuestions;
  }

  setStyles() {
    combineLatest({
      operatorsSettings: this.store.select(getOperatorsSettings),
      exhibitionSettings: this.store.select(getExhibitionSettings),
      isAdmin: this.store.select(isLoggedInAsAdmin),
      loginTimestamp: this.store.select(getLoginTimestamp)
    })
      .pipe(first(({ operatorsSettings }) => !isEmpty(operatorsSettings)))
      .subscribe(({ operatorsSettings, exhibitionSettings, isAdmin, loginTimestamp }) => {
        const isUserLoggedIn = !isLoginSessionExpired(loginTimestamp);

        if (exhibitionSettings) {
          this.appendSkinCSS(exhibitionSettings.customStyles, 3, false, exhibitionSettings.customStylesId);

          const queryParams: string = this.document.location.search.toLowerCase();

          if (
            isAdmin &&
            isUserLoggedIn &&
            (!queryParams || (!queryParams.includes('reg_mail') && !queryParams.includes('login_user')))
          ) {
            this.store.dispatch(new LoadStyle(exhibitionSettings.customStylesId));
          }
        } else if (Object.hasOwn(operatorsSettings, 'customStyles')) {
          this.appendSkinCSS(operatorsSettings.customStyles, 2, false, operatorsSettings.customStylesId);

          if (isAdmin && isUserLoggedIn) {
            this.store.dispatch(new LoadStyle(operatorsSettings.customStylesId));
          }
        }
      });
  }

  /* skin with higher or same priority rewrite lower or older priority
    1 - default color (red)
    2 - operators settings
    3 - event settings
    4 - voucher settings
    5 - admin editing styles
  */
  appendSkinCSS(variables, priority: number, isOperator: boolean, skinId) {
    this.store.pipe(select(isLoggedInAsAdmin), first()).subscribe((isAdmin) => {
      // add styles
      const cssString = generateSkinCss(variables);
      const cssSkin: CssSkinModel = {
        priority: priority,
        css: cssString,
        variables,
        id: skinId
      };

      if (isOperator) {
        isAdmin = isAdmin || isOperator;
      }

      this.store.dispatch(new SetExhibitionStyles({ cssSkin, isAdmin }));
    });
  }

  setVoucherStyles(styles) {
    this.appendSkinCSS(
      {
        ThemeColor: styles.ThemeColor,
        ThemeTxColor: styles.ThemeTxColor,
        AreaBlockBgActive: styles.AreaBlockBgActive
      },
      4,
      false,
      null
    );
  }

  /* JSON API */
  getCustomSettings(eventId) {
    if (eventId == '') {
      return of({});
    }

    const selfReg = this.helperService.isSelfRegistration() ? '?sr=true' : '';
    return this.http.get(`${environment.protocol}${environment.webApiUrl}/event/${eventId}/settings${selfReg}`).pipe(
      map((res) => {
        try {
          const settings: any = res || {};

          // TODO remove this fallback
          if (!Object.hasOwn(settings, 'isTicketHolderVisible')) {
            settings.isTicketHolderVisible = true;
          }

          return settings as any;
        } catch (err) {
          this.statusBarService.setStatus(err, 'error');
        }
      }),
      catchError((e) => {
        return this.errorHandlingService.errorHandler(e);
      })
    );
  }

  getLocalizedImages(eventId: number, countryCode: string) {
    return this.http.get(`${environment.protocol}${environment.webApiUrl}/event/${eventId}/images/${countryCode}`).pipe(
      map((res) => {
        try {
          return res || {};
        } catch (err) {
          this.statusBarService.setStatus(err, 'error');
        }
      }),
      catchError((e) => {
        return this.errorHandlingService.errorHandler(e);
      })
    );
  }

  getRandomSponsor(eventId: number, countryCode?: string): Observable<SponsorBannerModel> {
    return this.http.get<SponsorBannerModel>(
      `${environment.protocol}${environment.webApiUrl}/event/${eventId}/random-sponsor?language=${countryCode}`
    );
  }

  getOperatorSettings() {
    let selfReg = '';
    if (this.helperService.isSelfRegistration()) {
      selfReg = '?sr=true';
    }
    return this.http.get(`${environment.protocol}${environment.webApiUrl}/operator/settings${selfReg}`).pipe(
      map((res) => {
        try {
          return res as any;
        } catch (err) {
          this.statusBarService.setStatus(err, 'error');
        }
      }),
      catchError((e) => {
        return this.errorHandlingService.errorHandler(e);
      })
    );
  }

  getIsReadOnlyBuyerAndAccountInfo$() {
    return this.store.pipe(
      select(getSelectedExhibitionId),
      switchMap((exhibitionId) =>
        iif(
          () => !isNil(exhibitionId),
          this.store.select(selectExhibitionSettingsIsReadOnlyBuyerAndAccountInfo),
          this.store.select(selectOperatorSettingsIsReadOnlyBuyerAndAccountInfo)
        )
      )
    );
  }

  private setConfirmationCheckboxesOnTranslationChangeSubscription() {
    combineLatest({
      exhibitionSettings: this.exhibitionSettings$,
      isTranslationLoaded: this.store.select(getIsTranslationLoaded)
    })
      .pipe(
        filter(({ exhibitionSettings, isTranslationLoaded }) => !isEmpty(exhibitionSettings) && isTranslationLoaded),
        switchMap(({ exhibitionSettings }) =>
          this.translateService.stream(['confirmation.terms-conditions']).pipe(
            map((translations) => ({
              exhibitionSettings,
              translation: translations['confirmation.terms-conditions']
            }))
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ exhibitionSettings, translation }) => {
        const confirmationCheckboxes = prepareConfirmationCheckboxes(exhibitionSettings, translation);
        const confirmationPayload: FormInputsPayloadModel = {
          formInfo: [StepName.Confirmation, FormName.ConfirmationCheckboxes],
          inputSet: {
            rerender: false,
            list: confirmationCheckboxes
          }
        };

        this.store.dispatch(new SetInputs(confirmationPayload));
      });
  }
}
